import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type PortalDropdownProps = {
  anchorRef: React.RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  minWidth?: number;
  children: React.ReactNode;
};

export default function PortalDropdown({
  anchorRef,
  open,
  onClose,
  minWidth = 180,
  children
}: PortalDropdownProps) {
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<React.CSSProperties>({
    visibility: "hidden"
  });

  useEffect(() => {
    const el = document.createElement("div");
    el.style.position = "absolute";
    el.style.top = "0px";
    el.style.left = "0px";
    el.style.zIndex = "2147483647";
    portalRef.current = el;
    document.body.appendChild(el);
    return () => {
      if (portalRef.current) {
        document.body.removeChild(portalRef.current);
      }
    };
  }, []);

  // cerrar al click fuera o ESC
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (portalRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !portalRef.current) return;
    const place = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      const top = rect.bottom + window.scrollY + 6;
      const left = Math.max(8, rect.right + window.scrollX - minWidth);
      const willOverflowRight = rect.left + minWidth > window.innerWidth;
      const computedLeft = willOverflowRight
        ? left
        : rect.left + window.scrollX;
      setStyle({
        position: "absolute",
        top,
        left: computedLeft,
        minWidth,
        visibility: "visible"
      });
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(document.body);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, anchorRef, minWidth]);

  if (!open || !portalRef.current) return null;

  return createPortal(
    <div className="wa-title-dropdown-portal" style={style} role="menu">
      {children}
    </div>,
    portalRef.current
  );
}
